import { useEffect, useState } from 'react';
import { api, type OvenStatus } from '../lib/api';

/**
 * Tiny status chip for the poster oven (the owner's local baking box). Shown
 * wherever a made-up movie needs a generated poster: online = green dot, bake
 * away; offline = one-tap "Request it", which messages the host with an
 * approval link. Unobtrusive by design — renders nothing until the status
 * endpoint has answered.
 */
export function OvenChip() {
  const [status, setStatus] = useState<OvenStatus | null>(null);
  const [requested, setRequested] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api
        .ovenStatus()
        .then((s) => {
          if (alive) setStatus(s);
        })
        .catch(() => {
          /* chip is cosmetic — stay quiet on failure */
        });
    };
    load();
    const t = setInterval(load, 60_000); // the endpoint edge-caches 30s; this is cheap
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (!status) return null;

  if (status.online) {
    return (
      <span className="chip oven-chip" title={status.detail}>
        <span className="oven-dot on" aria-hidden="true" />
        🎨 Poster oven: online
      </span>
    );
  }

  // offline — a request may already be in flight (ours or someone else's)
  const pending = requested || Boolean(status.request);
  if (pending) {
    return (
      <span className="chip oven-chip">
        <span className="oven-dot" aria-hidden="true" />
        🎨 Requested — the host has been messaged
      </span>
    );
  }

  const request = async () => {
    setBusy(true);
    try {
      const r = await api.requestOven();
      if (r.ok) {
        if (r.reason === 'already-online') {
          setStatus((s) => (s ? { ...s, online: true } : s));
        } else {
          setRequested(true);
        }
      }
    } catch {
      /* leave the button up — tapping again retries */
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="chip oven-chip">
      <span className="oven-dot" aria-hidden="true" />
      🎨 Poster oven: offline
      <button className="oven-request" onClick={request} disabled={busy}>
        {busy ? 'Requesting…' : 'Request it'}
      </button>
    </span>
  );
}
